import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Box,
  Button,
  styled,
  CircularProgress,
  Link,
  Dialog,
  IconButton,
} from '@mui/material';
import ReactPlayer from 'react-player';
import { setVideoSelection, clearMoreClassSelection,  clearEndorsements } from '../../../redux/actions/WizardLaunchActions';
import PlayCircleFilledWhiteOutlinedIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import './VideoSelection.scss';
import profileIcon from '../../../assets/img/profile-icon.svg';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const OptionButton = styled(Button)(({ selected }) => ({
  border: selected ? '4px solid #1B56BB !important' : '',
}));

const VideoContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: '260px',
  height: '147px',
  borderRadius: '12px',
  overflow: 'hidden',
  marginBottom: '20px',
  cursor: 'pointer',
  border: '1px solid #E0E0E0',
  backgroundColor: '#fff',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.02)',
  },
});

const VideoOverlay = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '1000px',
    margin: '16px',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
}));
const PlayIconOverlay = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  opacity: 0.8,
  transition: 'opacity 0.3s ease',
  backgroundColor: 'rgba(0,0,0,0.5)',
  borderRadius: '50%',
  padding: 0,
  width: '60px',
  height: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  right: '10px',
  top: '10px',
  color: '#fff',
  zIndex: 2,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
});

const VideoGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '25px',
  margin: '0 auto',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    maxWidth: '450px',
  },
});

const VideoTitle = styled(Typography)({
  position: 'absolute',
  top: '10px',
  left: '10px',
  fontSize: '1rem',
  color: '#fff',
  zIndex: 1,
  fontWeight: 'bold',
  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  padding: '4px 8px',
  borderRadius: '4px',
});

function VideoSelection({ setFormState }) {
  const dispatch = useDispatch();
  const selectedVideo = useSelector(
    (state) => state.wizardLaunch.videoSelection
  );
  const dynamicData = useSelector(
    (state) => state.wizardLaunch.screenData.video
  );
  const isLoading = useSelector(
    (state) => state.wizardLaunch.dynamicDataLoading
  );
  const error = useSelector((state) => state.wizardLaunch.dynamicDataError);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  const stateSelection = useSelector(
    (state) => state.wizardLaunch.stateSelection
  );

  useEffect(() => {
    if (selectedVideo) {
      setPlayingVideo(selectedVideo.id);
    }
  }, [selectedVideo]);

  const handleVideoSelect = (option) => {
    dispatch(clearEndorsements());
    dispatch(clearMoreClassSelection());
    dispatch(setVideoSelection(option.answer));
    setFormState({
      question_id: option.next_question_id,
      userAnswer: {
        question_answer_id: option.question_answer_id,
        answer: option.answer,
        next_question_id: option.next_question_id,
        state_code_question_answer_id: stateSelection?.question_answer_id, // Include state code in form state
        state_code: stateSelection?.answer?.[0]?.id, // Include state code in form state
      },
      question_type:
        option.next_question_type || dynamicData?.question_type || 'video',
    });
  };

  const handleVideoClick = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVideoUrl(null);
    setPlayingVideo(null);
  };

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <Typography color='error'>
          Error loading videos. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className='video-section'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      <Typography
        variant='h5'
        gutterBottom
        sx={{
          textAlign: 'center',
          color: '#00326D',
          fontFamily: 'Barlow',
          fontSize: '24px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '32px',
          marginBottom: '52px',
        }}
      >
        {dynamicData?.question_text || 'Select a video to watch'}
      </Typography>

      <Box className='optionbtn' sx={{ display: 'flex', gap: 3, mb: 4 }}>
        {dynamicData?.options?.map((option) => {
          const isSelected = selectedVideo?.id === option.answer.id; // Define your condition here
          return (
            <OptionButton
              key={option.question_answer_id}
              variant='outlined'
              selected={isSelected} // Pass the selected prop based on the condition
              onClick={() => handleVideoSelect(option)}
            >
              <img src={profileIcon} alt='profile' />
              {option.answer.title}
            </OptionButton>
          );
        })}
      </Box>

      <Typography className='label-wrap'>
        <PlayCircleFilledWhiteOutlinedIcon className='play-btn-icon' />
        Unsure of which one?
        <Link>Click the videos to learn more.</Link>
      </Typography>

      <VideoGrid>
        {dynamicData?.options?.map((option) => (
          <VideoContainer
            key={option.question_answer_id}
            onClick={() => handleVideoClick(option.answer.videoUrl)}
          >
            <VideoTitle variant='h6'>{option.answer.title}</VideoTitle>
            <Box
              component='img'
              src={option.answer.thumbnail}
              alt={option.answer.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#fff',
              }}
            >
              <PlayIconOverlay className='play-icon'>
                <PlayArrowIcon sx={{ fontSize: 40, color: '#fff' }} />
              </PlayIconOverlay>
            </Box>
          </VideoContainer>
        ))}
      </VideoGrid>

      <VideoOverlay
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='lg'
        fullWidth
      >
        <CloseButton onClick={handleCloseDialog}>
          <CloseIcon />
        </CloseButton>
        <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
          <ReactPlayer
            url={selectedVideoUrl}
            width='100%'
            height='100%'
            playing={true}
            controls={true}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            config={{
              youtube: {
                playerVars: { showinfo: 1 },
              },
            }}
          />
        </Box>
      </VideoOverlay>
    </Box>
  );
}

export default VideoSelection;
